/*
 * PRD-037 — Seccion de entrega y pago de "Configurar comercio" (app SEPARADA, V4 inline).
 *
 * Replica lo esencial del DeliveryPaymentFormV2 del panel (acoplado a Redux + integraciones +
 * @plick/ui + Tailwind), reusando el mismo modelo de datos del Commerce: los 3 toggles de tipo de
 * entrega (`activar_*`), los metodos de pago por canal (`metodos_pago_*`) y los datos bancarios
 * (`bank_transfer_details`/CLABE) cuando se acepta transferencia. La validez se mide con el mismo
 * `validateDeliveryCommerceValues` del panel en el contenedor; aqui se renderiza la UI.
 *
 * Alcance acotado a los 3 metodos universales (efectivo/tarjeta/transferencia): enlace_pago y
 * payment_provider requieren integraciones que el borrador aun no tiene, asi que se omiten para no
 * ofrecer opciones inservibles. El dueno podra activarlas luego desde el panel.
 */
import { type CSSProperties, type ReactNode } from 'react';
import type { Commerce } from '@interfaces/general';
import { V4 } from '@components/v4/tokens';
import { C } from './ui';

type ChannelKey = 'activar_envio_domicilio' | 'activar_pasar_recoger' | 'activar_comer_restaurant';
type MethodsKey = 'metodos_pago_envio_domicilio' | 'metodos_pago_pasar_recoger' | 'metodos_pago_comer_restaurant';
type PayMethod = 'efectivo' | 'tarjeta' | 'transferencia';
type MetodosPago = Commerce['metodos_pago_envio_domicilio'];

const FONT = 'Poppins, sans-serif';

const CHANNELS: { channel: ChannelKey; methods: MethodsKey; label: string }[] = [
  { channel: 'activar_envio_domicilio', methods: 'metodos_pago_envio_domicilio', label: 'Envio a domicilio' },
  { channel: 'activar_pasar_recoger', methods: 'metodos_pago_pasar_recoger', label: 'Pasar a recoger' },
  { channel: 'activar_comer_restaurant', methods: 'metodos_pago_comer_restaurant', label: 'Comer en el restaurante' },
];

const PAY_METHODS: { value: PayMethod; label: string }[] = [
  { value: 'efectivo', label: 'Efectivo' },
  { value: 'tarjeta', label: 'Tarjeta' },
  { value: 'transferencia', label: 'Transferencia' },
];

const EMPTY_METHODS: MetodosPago = {
  efectivo: false,
  tarjeta: false,
  transferencia: false,
  enlace_pago: false,
  payment_provider: false,
};

const inputStyle: CSSProperties = {
  width: '100%',
  height: 38,
  padding: '0 12px',
  fontSize: 14,
  fontFamily: FONT,
  color: C.primary,
  border: `1px solid ${C.inputBorder}`,
  borderRadius: 6,
  boxSizing: 'border-box',
  outline: 'none',
};

interface Props {
  commerce: Commerce;
  handleUpdateValue: <K extends keyof Commerce>(key: K, value: Commerce[K]) => void;
}

const Toggle = ({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label: string }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    onClick={() => onChange(!checked)}
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      width: '100%',
      background: 'transparent',
      border: 'none',
      padding: 0,
      cursor: 'pointer',
      fontFamily: FONT,
    }}
  >
    <span style={{ fontSize: 14, fontWeight: 600, color: C.primary }}>{label}</span>
    <span
      aria-hidden="true"
      style={{
        width: 40,
        height: 22,
        borderRadius: 11,
        // Track OFF en gray (#BFD3D9): desacoplado de inputBorder (ahora grayLight, demasiado pálido
        // para un switch) para que el knob blanco conserve contraste.
        background: checked ? C.secondary : V4.gray,
        position: 'relative',
        flexShrink: 0,
        transition: 'background 140ms ease',
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: 2,
          left: checked ? 20 : 2,
          width: 18,
          height: 18,
          borderRadius: '50%',
          background: C.white,
          transition: 'left 140ms ease',
        }}
      />
    </span>
  </button>
);

const MethodCheckbox = ({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) => (
  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: C.primary, cursor: 'pointer' }}>
    <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
    {label}
  </label>
);

const Box = ({ children }: { children: ReactNode }) => (
  <div style={{ border: `1px solid ${C.cardBorder}`, borderRadius: 8, padding: 14, marginBottom: 12 }}>{children}</div>
);

export const ConfigureDeliverySection = ({ commerce, handleUpdateValue }: Props) => {
  const getMethods = (key: MethodsKey): MetodosPago => ({ ...EMPTY_METHODS, ...(commerce[key] || {}) });

  // Solo cuenta la transferencia de canales ACTIVOS: un canal apagado con `transferencia` stale no
  // debe forzar el form de datos bancarios (CLABE) para un metodo que el negocio no usara.
  const anyTransfer = CHANNELS.some(
    ({ channel, methods }) => Boolean(commerce[channel]) && getMethods(methods).transferencia,
  );

  const bank = commerce.bank_transfer_details || { clabe: '', titular_name: '', bank_name: '' };
  const updateBank = (field: 'clabe' | 'titular_name' | 'bank_name', value: string) =>
    handleUpdateValue('bank_transfer_details', { ...bank, [field]: value });

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <p style={{ fontSize: 13, color: C.muted, margin: '0 0 14px' }}>
        Activa los tipos de entrega y los metodos de pago que el negocio aceptara. El dueno podra ajustarlos despues.
      </p>

      {CHANNELS.map(({ channel, methods, label }) => {
        const enabled = Boolean(commerce[channel]);
        const m = getMethods(methods);
        const noMethod = enabled && !m.efectivo && !m.tarjeta && !m.transferencia;
        return (
          <Box key={channel}>
            <Toggle checked={enabled} onChange={(v) => handleUpdateValue(channel, v)} label={label} />
            {enabled && (
              <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${C.cardBorder}` }}>
                <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
                  {PAY_METHODS.map((pm) => (
                    <MethodCheckbox
                      key={pm.value}
                      label={pm.label}
                      checked={Boolean(m[pm.value])}
                      onChange={(v) => handleUpdateValue(methods, { ...m, [pm.value]: v })}
                    />
                  ))}
                </div>
                {noMethod && (
                  <p style={{ fontSize: 11, color: C.warning, margin: '8px 0 0' }}>
                    Selecciona al menos un metodo de pago.
                  </p>
                )}
              </div>
            )}
          </Box>
        );
      })}

      {anyTransfer && (
        <Box>
          <span style={{ fontSize: 14, fontWeight: 600, color: C.primary }}>Datos para transferencia</span>
          <p style={{ fontSize: 12, color: C.muted, margin: '4px 0 12px' }}>
            Necesarios para que el cliente pueda transferir. La CLABE debe tener 18 digitos.
          </p>
          <label style={{ display: 'block', marginBottom: 12 }}>
            <span style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 6 }}>
              Titular de la cuenta
            </span>
            <input
              style={inputStyle}
              value={bank.titular_name || ''}
              onChange={(e) => updateBank('titular_name', e.target.value)}
              placeholder="Nombre del titular"
            />
          </label>
          <label style={{ display: 'block', marginBottom: 12 }}>
            <span style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 6 }}>Banco</span>
            <input
              style={inputStyle}
              value={bank.bank_name || ''}
              onChange={(e) => updateBank('bank_name', e.target.value)}
              placeholder="Ej. BBVA"
            />
          </label>
          <label style={{ display: 'block' }}>
            <span style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 6 }}>CLABE (18 digitos)</span>
            <input
              style={inputStyle}
              inputMode="numeric"
              value={bank.clabe || ''}
              onChange={(e) => updateBank('clabe', e.target.value.replace(/\D/g, '').slice(0, 18))}
              placeholder="18 digitos"
            />
            {Boolean(bank.clabe) && bank.clabe.length !== 18 && (
              <span style={{ display: 'block', fontSize: 11, color: C.warning, marginTop: 6 }}>
                La CLABE debe tener 18 digitos.
              </span>
            )}
          </label>
        </Box>
      )}
    </div>
  );
};

export default ConfigureDeliverySection;
